// BL579 M4 — local pgvector sandbox for `grid dev`.
//
// When an app declares the managed vector substrate (`needs.vector` /
// `requires.vector`), `grid dev` spins a local pgvector container and
// injects `VECTOR_PGVECTOR_URL` so the app's code connects exactly as it will
// in production. Same engine major as prod (pgvector on Postgres 16,
// `pgvector/pgvector:pg16`).
//
// Everything here shells out through an injected `spawnSync` so the wiring is
// unit-testable without a real Docker daemon. Failures are SOFT: the caller
// prints a friendly skip and `grid dev` continues without the env var —
// a missing Docker daemon must never crash the dev runner.

import type { spawnSync as defaultSpawnSync } from 'node:child_process';

type SpawnSyncFn = typeof defaultSpawnSync;

/** Prod-parity image: pgvector on Postgres 16. */
export const PGVECTOR_IMAGE = 'pgvector/pgvector:pg16';
/** The container's Postgres port (mapped to an auto-picked host port). */
export const PGVECTOR_CONTAINER_PORT = 5432;
/** Local-only credentials — never used anywhere but the dev container. */
export const PGVECTOR_LOCAL_USER = 'appuser';
export const PGVECTOR_LOCAL_PASSWORD = 'local';

/** The container name for an app's dev pgvector instance (one per app). */
export function pgvectorContainerName(appSlug: string): string {
  return `cloudgrid-dev-pgvector-${appSlug}`;
}

/**
 * The connection URL injected as `VECTOR_PGVECTOR_URL`. Mirrors the prod
 * `postgres://` shape; `sslmode=disable` because the local container serves
 * plaintext. The per-app database is named after the app slug.
 */
export function buildVectorUrl(port: number, appSlug: string): string {
  return `postgres://${PGVECTOR_LOCAL_USER}:${PGVECTOR_LOCAL_PASSWORD}@localhost:${port}/${appSlug}?sslmode=disable`;
}

/**
 * Whether a usable Docker daemon is reachable. `docker info` exits non-zero
 * when the CLI is absent or the daemon is down — exactly the "Docker is not
 * running" case the caller reports.
 */
export function isDockerAvailable(spawnSync: SpawnSyncFn): boolean {
  try {
    const r = spawnSync('docker', ['info'], { stdio: 'ignore' });
    return r.status === 0;
  } catch {
    return false;
  }
}

export interface PgvectorSandbox {
  containerName: string;
  port: number;
  url: string;
}

export interface StartPgvectorOptions {
  appSlug: string;
  /** Host port to map onto the container's 5432 (caller auto-picks a free one). */
  port: number;
  spawnSync: SpawnSyncFn;
  /** Health-poll bounds (defaults: 40 attempts × 500ms ≈ 20s). */
  maxAttempts?: number;
  pollIntervalMs?: number;
  /** Injectable sleep for tests. Defaults to a real timer. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Start the local pgvector container and wait until Postgres accepts
 * connections (`pg_isready`). Returns the sandbox handle on success, or `null`
 * on any failure (image pull error, port conflict, never-healthy) — the caller
 * treats `null` as "skip injection, keep going". Any half-created container is
 * removed before returning `null`.
 */
export async function startPgvectorSandbox(
  opts: StartPgvectorOptions,
): Promise<PgvectorSandbox | null> {
  const { appSlug, port, spawnSync } = opts;
  const name = pgvectorContainerName(appSlug);
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const maxAttempts = opts.maxAttempts ?? 40;
  const pollIntervalMs = opts.pollIntervalMs ?? 500;

  // Clear any stale same-named container from a crashed prior dev run so
  // `docker run --name` doesn't conflict. Best-effort.
  spawnSync('docker', ['rm', '-f', name], { stdio: 'ignore' });

  const run = spawnSync(
    'docker',
    [
      'run',
      '--rm',
      '-d',
      '--name',
      name,
      '-e',
      `POSTGRES_PASSWORD=${PGVECTOR_LOCAL_PASSWORD}`,
      '-e',
      `POSTGRES_USER=${PGVECTOR_LOCAL_USER}`,
      '-e',
      `POSTGRES_DB=${appSlug}`,
      '-p',
      `${port}:${PGVECTOR_CONTAINER_PORT}`,
      PGVECTOR_IMAGE,
    ],
    { stdio: 'ignore' },
  );
  if (run.status !== 0) {
    spawnSync('docker', ['rm', '-f', name], { stdio: 'ignore' });
    return null;
  }

  // Poll pg_isready inside the container until Postgres is accepting
  // connections — `docker run -d` returns as soon as the container is created,
  // well before Postgres has initialized the database.
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const ready = spawnSync(
      'docker',
      ['exec', name, 'pg_isready', '-U', PGVECTOR_LOCAL_USER, '-d', appSlug],
      { stdio: 'ignore' },
    );
    if (ready.status === 0) {
      return { containerName: name, port, url: buildVectorUrl(port, appSlug) };
    }
    await sleep(pollIntervalMs);
  }

  // Never became healthy — tear down and signal failure.
  spawnSync('docker', ['rm', '-f', name], { stdio: 'ignore' });
  return null;
}

/** Stop + remove the sandbox container. Best-effort; never throws. */
export function stopPgvectorSandbox(containerName: string, spawnSync: SpawnSyncFn): void {
  try {
    spawnSync('docker', ['rm', '-f', containerName], { stdio: 'ignore' });
  } catch {
    // Never block dev shutdown on a docker hiccup.
  }
}
